// @ts-nocheck
import { ReactNode } from 'react';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center } from '@/components/ui/layout';
import { Title, Text, Badge } from '@/components/ui/core';
import { IconCheck } from '@tabler/icons-react';

export interface Feature {
  icon?: ReactNode;
  title: string;
  description: string;
}

interface SidebarLayoutProps {
  children: ReactNode;
  logo?: ReactNode;
  title?: string;
  subtitle?: string;
  sidebarContent?: ReactNode;
  sidebarWidth?: '25%' | '30%' | '35%';
  sidebarBg?: string;
  showFeatures?: boolean;
  features?: Feature[];
  footerText?: string;
}

export default function SidebarLayout({
  children,
  logo,
  title = 'Welcome Back',
  subtitle,
  sidebarContent,
  sidebarWidth = '30%',
  sidebarBg,
  showFeatures = true,
  features = [],
  footerText = '\u00a9 2025 Your Company. All rights reserved.',
}: SidebarLayoutProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const defaultSidebarBg = isDark
    ? `linear-gradient(135deg, ${theme.colors.dark[8]}, ${theme.colors.dark[7]})`
    : `linear-gradient(135deg, ${theme.colors.indigo[6]}, ${theme.colors.cyan[5]})`;

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left Sidebar */}
      <Box
        style={{
          width: sidebarWidth,
          minHeight: '100vh',
          background: sidebarBg || defaultSidebarBg,
          color: 'white',
          padding: '2rem',
          display: 'none',
        }}
        visibleFrom="md"
      >
        <Stack justify="space-between" h="100%">
          {/* Top Section - Logo & Title */}
          <Box>
            {logo && <Box mb="xl">{logo}</Box>}

            {!sidebarContent && (
              <Stack gap="lg" mt="xl">
                <div>
                  <Title order={2} c="white" mb="sm">
                    {title}
                  </Title>
                  {subtitle && (
                    <Text size="lg" c="white" style={{ opacity: 0.9 }}>
                      {subtitle}
                    </Text>
                  )}
                </div>

                {/* Features List */}
                {showFeatures && features.length > 0 && (
                  <Stack gap="md" mt="xl">
                    {features.map((feature, index) => (
                      <Box key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                        <Center
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            flexShrink: 0,
                          }}
                        >
                          {feature.icon || <IconCheck size={20} />}
                        </Center>
                        <div>
                          <Text fw={600} c="white" mb={4}>
                            {feature.title}
                          </Text>
                          <Text size="sm" c="white" style={{ opacity: 0.85 }}>
                            {feature.description}
                          </Text>
                        </div>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}

            {/* Custom Sidebar Content */}
            {sidebarContent}
          </Box>

          {/* Bottom Section - Footer */}
          {footerText && (
            <Text size="xs" c="white" style={{ opacity: 0.7 }}>
              {footerText}
            </Text>
          )}
        </Stack>
      </Box>

      {/* Right Side - Form Content */}
      <Box
        style={{
          flex: 1,
          minHeight: '100vh',
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
